import * as React from "react";

import { type InputFieldVariantProps } from "@/components/ui/field-variants";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { fieldDescribedByIds } from "@/lib/field-described-by";
import { cn } from "@/lib/utils";
import { RequiredMarker } from "@/components/required-fields-legend"

export type CustomTextareaProps = Omit<React.ComponentProps<"textarea">, keyof InputFieldVariantProps> &
  InputFieldVariantProps & {
    /** Visible label text paired with the control (also used for the `Label`) */
    label: React.ReactNode
    /** When set, appends a visual asterisk after the label. */
    required?: boolean
    /**
     * Validation copy for the reserved error row. When omitted, the row still occupies space so
     * showing a message later does not shift layout.
     */
    errorMessage?: string
    /** `id` on the error paragraph (referenced by `aria-describedby` when `errorMessage` is set) */
    errorId?: string
    hintId: string
    hintText: string
    /** Optional classes for the error row height (defaults reserve roughly one line of `body-copy-s`) */
    errorReserveClassName?: string
    className?: string
    labelClassName?: string
    /** Classes merged onto the inner `Textarea` (e.g. width, border) */
    textareaClassName?: string
    /** Classes merged onto the outer `div` (e.g. padding) */
    paddingClassName?: string
    children?: React.ReactNode
  }

/**
 * Composed multi-line field: `Textarea` + `Label`, a screen-reader-only hint, and a validation line.
 *
 * Follows the same pattern as `CustomInput`.
 */
const CustomTextarea = React.forwardRef<HTMLTextAreaElement, CustomTextareaProps>(function CustomTextarea(
  {
    label,
    required,
    errorMessage,
    errorId = "error-id",
    hintId,
    hintText,
    errorReserveClassName,
    className,
    labelClassName,
    textareaClassName,
    variant = "floating",
    id,
    "aria-describedby": ariaDescribedByProp,
    paddingClassName = "pb-4",
    children,
    ...textareaProps
  },
  ref
) {
  const hasError = Boolean(errorMessage)
  const describedBy = fieldDescribedByIds({ errorId, hasError, hintId })

  return (
    <div className={cn("flex w-full min-w-0 flex-col mb-4", className)}>
      <div className={cn("relative", paddingClassName)}>
        <div className="relative">
          <Textarea
            ref={ref}
            id={id}
            variant={variant}
            required={required}
            {...textareaProps}
            aria-invalid={hasError}
            aria-describedby={ariaDescribedByProp ?? describedBy}
            className={cn(
              variant === "floating" && "floating-input py-4",
              textareaClassName
            )}
          />
          <Label htmlFor={id} variant={variant} data-error={hasError} className={labelClassName}>
            {label}
            {required ? <RequiredMarker /> : null}
          </Label>
          <span id={hintId} className="sr-only">
            {hintText}
          </span>
        </div>
        {hasError && !children ? (
          <p
            id={errorId}
            className={cn(
              "body-copy-s text-destructive absolute bottom-3 left-0 right-0 whitespace-nowrap",
              errorReserveClassName
            )}
          >
            {errorMessage}
          </p>
        ) : null}
        {children}
      </div>
    </div>
  )
})

export { CustomTextarea }
